import React from 'react';
import { Typography } from '../typography';
import { Popover } from '../popover';
import { styledComponent } from '../../hoc';
import { DateUtil } from '@dewise-devex/backstage-plugin-utilities-common-client';

/**
 * BadgeProps defines the properties for the Badge component.
 */
export type BadgeProps = {
  /**
   * The size of the badge (optional). Defaults to 'large'.
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * The name of the badge.
   */
  name: string;

  /**
   * The image of the badge.
   */
  img: React.ReactNode;

  /**
   * The description of the badge.
   */
  description: string;

  /**
   * The date the badge was awarded.
   */
  dateAwarded: string;

  /**
   * The version of the badge
   */
  version: string;
};

/**
 * Styled component for the badge content wrapper.
 *
 * @returns A styled div element with the content layout for the badge.
 */
const Content = styledComponent('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '24px',
}));

/**
 * Styled component for the badge image.
 *
 * @param {number} size - The size of the badge image (height and width).
 *
 * @returns A styled img element to display the badge image.
 */
const Image = styledComponent<JSX.IntrinsicElements['img'] & { size: number }>(
  'img',
  { ignoreProps: ['size'] },
)(({ size }) => ({
  marginTop: size < 32 ? undefined : '12px',
  height: size,
  width: size,
  objectFit: 'contain',

  '& > svg': {
    height: size,
    width: size,
    objectFit: 'contain',
  },
  '& > img': {
    height: size,
    width: size,
    objectFit: 'contain',
  },
}));

const sizeMap = {
  small: 30,
  medium: 32,
  large: 64,
};

/**
 * Badge component to display a badge with an image, description, and details.
 *
 * The badge content will show in a popover when the badge image is hovered over.
 *
 * @param {BadgeProps} props - The properties passed to the Badge component.
 *
 * @returns {React.ReactElement} The rendered Badge component.
 *
 * @category Component
 * @document BadgeComponentUsage.md
 */
export const Badge = (props: BadgeProps): React.ReactElement => {
  return (
    <Popover
      Component={componentProps => (
        <Image
          src={props.img as string}
          alt={props.name}
          size={sizeMap[props.size ?? 'large']}
          {...componentProps}
        />
      )}
    >
      <BadgeInformation {...props} />
    </Popover>
  );
};

export const BadgeInformation = (props: BadgeProps): React.ReactElement => {
  const formattedDate = DateUtil.toShortDate(props.dateAwarded);

  return (
    <Content>
      <Image src={props.img as string} alt={props.name} size={118} />
      <Typography variant="titleMD" sx={{ marginTop: 3 }}>
        {props.name}
      </Typography>
      <Typography variant="bodyMD">{props.description}</Typography>
      <Typography variant="bodySM" sx={{ marginTop: 3 }}>
        {`Awarded on ${formattedDate}`}
      </Typography>
      <Typography variant="bodySM">{`Badge v. ${props.version}`}</Typography>
    </Content>
  );
};
